import React from 'react';
import Icon from 'components/AppIcon';

const STATUS_COLORS = {
  enrolled: { bg: 'bg-primary', text: 'text-primary', label: 'Enrolled', icon: 'BookOpen' },
  in_progress: { bg: 'bg-accent', text: 'text-accent', label: 'In Progress', icon: 'PlayCircle' },
  completed: { bg: 'bg-success', text: 'text-success', label: 'Completed', icon: 'CheckCircle2' },
  not_started: { bg: 'bg-muted', text: 'text-muted-foreground', label: 'Not Started', icon: 'Circle' },
};

export default function TrainingProgressIndicator({
  courseName = '',
  progress = 0,          // 0-100
  status = 'not_started',
  totalModules = 0,
  completedModules = 0,
  compact = false,
}) {
  const clampedProgress = Math.min(100, Math.max(0, progress));
  const statusConfig = STATUS_COLORS?.[status] || STATUS_COLORS?.not_started;

  if (compact) {
    return (
      <div className="flex items-center gap-2" aria-label={`${courseName} progress: ${clampedProgress}%`}>
        <div className="progress-bar-track flex-1 min-w-[60px]">
          <div
            className={`progress-bar-fill ${statusConfig?.bg}`}
            style={{ width: `${clampedProgress}%` }}
            role="progressbar"
            aria-valuenow={clampedProgress}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
        <span className={`font-data text-xs font-medium ${statusConfig?.text} w-8 text-right`}>
          {clampedProgress}%
        </span>
      </div>
    );
  }

  return (
    <div
      className="bg-card border border-border rounded-md p-4 transition-shadow duration-[250ms] ease-smooth hover:shadow-md"
      aria-label={`Training progress for ${courseName}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          {courseName && (
            <p className="font-caption text-sm font-semibold text-foreground truncate mb-1">
              {courseName}
            </p>
          )}
          <div className={`inline-flex items-center gap-1.5 font-caption text-xs font-medium ${statusConfig?.text}`}>
            <Icon name={statusConfig?.icon} size={13} color="currentColor" />
            {statusConfig?.label}
          </div>
        </div>
        <span className={`font-data text-lg font-semibold ${statusConfig?.text} flex-shrink-0`}>
          {clampedProgress}%
        </span>
      </div>
      {/* Progress Bar */}
      <div className="progress-bar-track mb-2">
        <div
          className={`progress-bar-fill ${statusConfig?.bg}`}
          style={{ width: `${clampedProgress}%` }}
          role="progressbar"
          aria-valuenow={clampedProgress}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${clampedProgress}% complete`}
        />
      </div>
      {/* Module Count */}
      {totalModules > 0 && (
        <div className="flex items-center justify-between">
          <p className="font-caption text-xs text-muted-foreground">
            {completedModules} of {totalModules} modules complete
          </p>
          {status === 'completed' && (
            <div className="flex items-center gap-1 text-success">
              <Icon name="CheckCircle2" size={13} color="currentColor" />
              <span className="font-caption text-xs font-medium">Done</span>
            </div>
          )}
          {status !== 'completed' && clampedProgress > 0 && (
            <p className="font-caption text-xs text-muted-foreground">
              {totalModules - completedModules} remaining
            </p>
          )}
        </div>
      )}
      {/* Next Step Hint */}
      {status === 'not_started' && (
        <div className="mt-2 pt-2 border-t border-border flex items-center gap-1.5">
          <Icon name="ArrowRight" size={12} color="var(--color-primary)" />
          <p className="font-caption text-xs text-primary">Start this course to begin your training journey</p>
        </div>
      )}
    </div>
  );
}